import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useRoute } from 'vue-router';
import { useStorage } from '@vueuse/core';
import { secondsToHms } from '~/helpers/date';
import useStore from '~/composables/useStore';
import { resolvedPeriodBoundaries } from '~/composables/navigation-next/productionPeriod';

// Extend dayjs with the UTC plugin
dayjs.extend(utc);

export default function useDashboard() {
  // Listen to store variable.
  const store = useStore();
  const openingHours = computed(() => store.getters['navigation/openingHours']);
  const periodTypeSelected = computed(() => store.getters['navigation/periodTypeSelected']);
  const periodValue = computed(() => store.getters['navigation/period_value']);
  const limitToNow = ref(true);
  const selectedGroups = computed(() => store.getters['navigation/groupsSelected']);
  const selectedGroup = computed(() => store.getters['navigation/groupSelected']);
  const factorySelected = computed(() => store.getters['navigation/factorySelected']);
  const selectedMachines = computed(() => {
    if (selectedGroup.value?.group_id === 0 && factorySelected.value) {
      return factorySelected.value.groups[0].machines.map((machine) => machine.machine_id);
    }
    return [];
  });
  const selectedMachine = computed(() => store.getters['navigation/machineSelected']);
  const deviceIsKeyVibe = computed(() => selectedMachine.value?.machine_devices[0]?.device_code === 'KeyVibe');
  const deviceCountTypeIsOnOff = computed(() => selectedMachine.value?.machine_count_type?.mct_name === 'on_off');
  const route = useRoute();

  /**
   * Get the start and end values depending on the type of period selected
   */
  const selectedPeriodBoundaries = computed(() => {
    // Bounds resolved by nav-next (E-0102) take priority; this legacy
    // computation only runs when the new navigation is not active.
    const resolvedBoundaries = resolvedPeriodBoundaries(
      store.getters['navigation/resolvedPeriod'],
      { limitToNow: limitToNow.value },
    );
    if (resolvedBoundaries) {
      return resolvedBoundaries;
    }

    let start = dayjs().utc();
    let startDate;
    let end = dayjs().utc();
    const now = dayjs().utc();

    const rawPeriodValue = periodValue.value[periodTypeSelected.value];
    const periodOffset = dayjs(rawPeriodValue)
      .startOf('day')
      .utcOffset();
    const endPeriodOffset = dayjs(rawPeriodValue)
      .endOf('day')
      .utcOffset();

    // If we have opening hours, we need to use them to calculate the start and end of the period.
    if (openingHours.value) {
      const formattedStart = `${rawPeriodValue} ${secondsToHms(openingHours.value.start)}`;
      const formattedEnd = `${rawPeriodValue} ${secondsToHms(openingHours.value.end)}`;
      start = dayjs(formattedStart).subtract(endPeriodOffset, 'm');
      end = dayjs(formattedEnd).subtract(endPeriodOffset, 'm');
    } else {
      // Read tenant creation date from window object.
      const tenantCreation = window.tenant
        ? dayjs(window.tenant.tenant_creation)
        : dayjs();

      // For each boundary we need to calculate the exact date time in UTC.
      switch (periodTypeSelected.value) {
        case 'now':
        case 'Day':
          start = dayjs(rawPeriodValue)
            .startOf('day')
            .subtract(periodOffset, 'm');
          end = dayjs(rawPeriodValue)
            .endOf('day')
            .subtract(endPeriodOffset, 'm');
          break;
        case 'Week':
        case 'Custom':
          start = dayjs(rawPeriodValue[0])
            .startOf('day')
            .subtract(dayjs(rawPeriodValue[0])
              .utcOffset(), 'm');
          end = dayjs(rawPeriodValue[1])
            .endOf('day')
            .subtract(dayjs(rawPeriodValue[1])
              .utcOffset(), 'm');
          break;
        case 'Month':
          startDate = dayjs(rawPeriodValue)
            .startOf('month')
            .subtract(periodOffset, 'm');
          if (tenantCreation.isBefore(startDate)) {
            start = startDate;
          } else {
            start = tenantCreation.subtract(periodOffset, 'm');
          }
          end = dayjs(rawPeriodValue)
            .endOf('month')
            .subtract(periodOffset, 'm');
          break;
        case 'Year':
          startDate = dayjs(rawPeriodValue)
            .startOf('year')
            .subtract(periodOffset, 'm');
          if (tenantCreation.isBefore(startDate)) {
            start = startDate;
          } else {
            start = tenantCreation.subtract(periodOffset, 'm');
          }
          end = dayjs(rawPeriodValue)
            .endOf('year').subtract(endPeriodOffset, 'm');
          break;
        default:
          console.error(`Unexpected period type: ${periodTypeSelected.value}`);
          break;
      }
    }

    // `start`/`end` above are built in "naive UTC" space: their INSTANT is
    // shifted by the browser offset so that their format() yields the UTC wall
    // clock. Comparing them to `now`, a true instant, is therefore off by that
    // offset — which used to invert the window during the offset-wide slot
    // before local midnight. Bring `now` into the same space to compare.
    const nowNaiveUtc = dayjs(now.format('YYYY-MM-DD HH:mm:ss'));

    // If the end date is after now and we want now as last time of the period,
    // we set it to now — but only for a window that has STARTED: clamping a
    // fully future one would return end < start, which the backend refuses.
    if (limitToNow.value && nowNaiveUtc.isBefore(end) && nowNaiveUtc.isAfter(start)) {
      end = now;
    }

    // If the start date is after the end date, we add one day to the end date for nigth shift.
    if (start.isAfter(end) || openingHours.value?.isNightHours) {
      end = end.add(1, 'day');
    }

    return {
      start: start.format('YYYY-MM-DD HH:mm:ss'),
      end: end.format('YYYY-MM-DD HH:mm:ss'),
    };
  });

  /**
   * The current dashboard id
   */
  const currentDashboardId = computed(() => {
    if (route.name !== 'dashboardId') {
      return undefined;
    }

    return route.params.value;
  });

  /**
   * The dashboards directly assigned to the current user.
   */
  const userDashboards = computed(() => store?.getters['dashboard/getUserDashboards'] ?? []);
  const allDashboards = computed(() => store?.getters['dashboard/getDashboards'] ?? []);

  /**
   * Find a dashboard by its id among all the dashboards, then the current dashboard
   * (fetched by its id: the client-specific dashboards are never listed).
   * @param {string|number} id
   * @returns {Object|undefined}
   */
  function findDashboard(id) {
    return store?.getters['dashboard/getDashboard']?.(id);
  }

  /**
   * The dashboard selected in the route params.
   */
  const selectedDashboard = computed(() => findDashboard(route.params.value));

  /**
     * Translate the dashboards to the current user locale
     */
  function sortDashboards(dashboards) {
    return dashboards
      .slice() // To avoid mutating the original array
      .sort(
        // eslint-disable-next-line max-len
        (a, b) => a.title.toLowerCase()
          .localeCompare(b.title.toLowerCase()),
      );
  }
  /**
   * Returns all dashboards with translation
   */
  const dashboards = computed(() => sortDashboards(allDashboards.value));

  /**
   * Returns all dashboards or userDashboards
   */
  const sortedUserDashboards = computed(() => {
    if (userDashboards.value.length) {
      return sortDashboards(userDashboards.value);
    }

    return dashboards.value;
  });

  /**
   * Equipments reactive variable. We should only use it in the old dashboards.
   */
  const equipments = useStorage('oldDashboardEquipments', []);
  /**
   * Equipment type reactive variable. We should only use it in the old dashboards.
   */
  const equipmentType = useStorage('oldDashboardEquipmentType', null);

  return {
    currentDashboardId,
    equipments,
    equipmentType,
    limitToNow,
    selectedGroup,
    selectedGroups,
    selectedMachines,
    selectedMachine,
    selectedPeriodBoundaries,
    periodTypeSelected,
    deviceIsKeyVibe,
    deviceCountTypeIsOnOff,
    sortedUserDashboards,
    sortDashboards,
    selectedDashboard,
    findDashboard,
    userDashboards,
  };
}
